package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.CellStatus
import er.prateek.crossly.game.crossword.CrosswordCellState
import er.prateek.crossly.game.crossword.CrosswordEngine
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.IconChevron
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import kotlin.math.floor

private val CellGap = 3.dp

/** Margin strip sizes, expressed in cells so they scale with the board. */
private const val GutterWidthRatio = 1.75f
private const val GutterHeightRatio = 1.45f

/**
 * The board, optionally wrapped in its clue margins.
 *
 * Cell size is solved from the space actually available — the largest square
 * that fits both the width and the height — so the same composable renders a
 * 5x5 on a phone and a 15x15 on a desktop window without scrolling or clipping,
 * and re-solves live as a desktop window is dragged. When a [gutter] is passed,
 * the clue strips are folded into that same solve (as fractions of a cell), so
 * turning them on shrinks the squares rather than pushing the board off screen.
 */
@Composable
fun CrosswordGridView(
    engine: CrosswordEngine,
    modifier: Modifier = Modifier,
    highlightedCells: Set<CellPos> = emptySet(),
    gutter: ClueGutter? = null,
    activeClue: Clue? = null,
    activeDirection: ClueDirection = ClueDirection.ACROSS,
    onSelectClue: (Clue, ClueDirection) -> Unit = { _, _ -> },
    dragState: LetterDragState? = null,
    minCellSize: Dp = 26.dp,
    maxCellSize: Dp = 68.dp,
) {
    val colors = MaterialTheme.game
    val showGutter = gutter != null && !gutter.isEmpty

    BoxWithConstraints(modifier = modifier, contentAlignment = Alignment.Center) {
        val framePadding = 5.dp
        val marginTracks = if (showGutter) 1 else 0
        val horizontalSlack = CellGap * (engine.width + marginTracks - 1) + framePadding * 2
        val verticalSlack = CellGap * (engine.height + marginTracks - 1) + framePadding * 2
        val widthUnits = engine.width + if (showGutter) GutterWidthRatio else 0f
        val heightUnits = engine.height + if (showGutter) GutterHeightRatio else 0f

        val byWidth = (maxWidth - horizontalSlack) / widthUnits
        // Inside a scrolling parent the height is unbounded; width alone decides then.
        val byHeight = if (constraints.hasBoundedHeight) {
            (maxHeight - verticalSlack) / heightUnits
        } else {
            maxCellSize
        }
        val cellSize = minOf(byWidth, byHeight, maxCellSize).coerceAtLeast(minCellSize)
        val gutterWidth = cellSize * GutterWidthRatio
        val gutterHeight = cellSize * GutterHeightRatio

        Column(
            modifier = Modifier
                .clip(RoundedCornerShape(16.dp))
                .background(colors.cellBorder.copy(alpha = 0.55f))
                .padding(framePadding),
            verticalArrangement = Arrangement.spacedBy(CellGap),
        ) {
            if (showGutter) {
                Row(horizontalArrangement = Arrangement.spacedBy(CellGap)) {
                    // Dead corner: the square where the two margins meet.
                    Box(modifier = Modifier.width(gutterWidth).height(gutterHeight))
                    for (col in 0 until engine.width) {
                        ClueGutterCell(
                            slot = gutter.topByCol[col],
                            engine = engine,
                            width = cellSize,
                            height = gutterHeight,
                            isActive = gutter.topByCol[col].matches(activeClue, activeDirection),
                            onSelect = onSelectClue,
                        )
                    }
                }
            }

            for (row in 0 until engine.height) {
                Row(horizontalArrangement = Arrangement.spacedBy(CellGap)) {
                    if (showGutter) {
                        ClueGutterCell(
                            slot = gutter.leftByRow[row],
                            engine = engine,
                            width = gutterWidth,
                            height = cellSize,
                            isActive = gutter.leftByRow[row].matches(activeClue, activeDirection),
                            onSelect = onSelectClue,
                        )
                    }
                    for (col in 0 until engine.width) {
                        val pos = CellPos(row, col)
                        val cell = engine.cellAt(pos)
                        if (cell == null || cell.isBlack) {
                            BlockCell(size = cellSize)
                        } else {
                            LetterCell(
                                cell = cell,
                                size = cellSize,
                                isSelected = engine.selected == pos,
                                isInWord = pos in highlightedCells,
                                isDropTarget = dragState?.hoveredCell == pos,
                                dragState = dragState,
                                // Tapping the square you're already on takes your
                                // letter back off the board — the counterpart to
                                // dragging one in. With nothing of yours there,
                                // it stays the direction toggle it has always been.
                                onClick = {
                                    val takeBack = engine.selected == pos && !cell.isGiven && cell.userChar != null
                                    if (takeBack) {
                                        val removed = engine.clearLetterAt(pos)
                                        val from = dragState?.boundsOf(pos)
                                        if (removed != null && from != null) {
                                            dragState.flyToRack(removed, from)
                                        }
                                    } else {
                                        engine.selectCell(pos)
                                    }
                                },
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun GutterSlot?.matches(clue: Clue?, direction: ClueDirection): Boolean =
    this != null && clue != null && this.direction == direction &&
        this.clue.row == clue.row && this.clue.col == clue.col

/**
 * One clue sitting in the margin, with an arrow pointing along its answer.
 *
 * The text is deliberately tiny and clipped: this strip is for recognising a
 * clue at a glance and tapping into it, while the clue bar above the board
 * always carries the full wording of whichever one is active.
 */
@Composable
private fun ClueGutterCell(
    slot: GutterSlot?,
    engine: CrosswordEngine,
    width: Dp,
    height: Dp,
    isActive: Boolean,
    onSelect: (Clue, ClueDirection) -> Unit,
) {
    val colors = MaterialTheme.game
    val shape = RoundedCornerShape(minOf(width, height) * 0.18f)

    if (slot == null) {
        Box(modifier = Modifier.width(width).height(height))
        return
    }

    val solved = engine.isClueSolved(slot.clue, slot.direction)
    val fill by animateColorAsState(
        when {
            isActive -> colors.accentSoft
            solved -> colors.card.mixWith(colors.success, 0.14f)
            else -> colors.card
        },
        tween(180),
        label = "gutterFill",
    )
    val textColor = when {
        isActive -> colors.ink
        solved -> colors.inkMuted
        else -> colors.inkMuted
    }

    val fontSize = (minOf(width, height).value * 0.21f).coerceIn(7.5f, 11f)
    val lineHeight = fontSize * 1.15f
    // The arrow owns the edge it points from; the text stops short of it rather
    // than running underneath.
    val across = slot.direction == ClueDirection.ACROSS
    val arrowInset = 11.dp
    // Fill what's left with as many lines as it can actually show, so nothing is
    // clipped mid-line.
    val verticalRoom = height.value - 4f - if (across) 0f else arrowInset.value
    val maxLines = floor(verticalRoom / lineHeight).toInt().coerceAtLeast(1)

    Box(
        modifier = Modifier
            .width(width)
            .height(height)
            .clip(shape)
            .background(fill)
            .border(if (isActive) 1.5.dp else 1.dp, if (isActive) colors.accent else colors.cardBorder, shape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = { onSelect(slot.clue, slot.direction) },
            ),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = slot.clue.clue,
            fontSize = fontSize.sp,
            lineHeight = lineHeight.sp,
            fontWeight = if (isActive) FontWeight.SemiBold else FontWeight.Medium,
            color = textColor,
            textAlign = TextAlign.Center,
            maxLines = maxLines,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.padding(
                start = 3.dp,
                end = if (across) arrowInset else 3.dp,
                top = 2.dp,
                bottom = if (across) 2.dp else arrowInset,
            ),
        )

        // The arrow sits on the edge the answer runs into, which is what tells the
        // player whether this clue belongs to the row or the column.
        val arrowAlignment = if (across) Alignment.CenterEnd else Alignment.BottomCenter
        Box(modifier = Modifier.align(arrowAlignment).padding(1.dp)) {
            if (solved) {
                IconCheck(size = 9.dp, tint = colors.success)
            } else {
                IconChevron(
                    size = 9.dp,
                    tint = if (isActive) colors.accent else colors.inkMuted,
                    pointsLeft = false,
                    modifier = if (slot.direction == ClueDirection.DOWN) Modifier.rotate(90f) else Modifier,
                )
            }
        }
    }
}

@Composable
private fun BlockCell(size: Dp) {
    val colors = MaterialTheme.game
    Box(
        modifier = Modifier
            .size(size)
            .clip(RoundedCornerShape(size * 0.14f))
            .background(colors.cellBlock),
    )
}

@Composable
private fun LetterCell(
    cell: CrosswordCellState,
    size: Dp,
    isSelected: Boolean,
    isInWord: Boolean,
    isDropTarget: Boolean,
    dragState: LetterDragState?,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game

    // A square being dragged over outranks everything: the player needs to know
    // where the letter will land before they let go. Selection comes next, then
    // status, then the word highlight — the cursor must never be lost because a
    // cell was just checked.
    val targetFill = when {
        isDropTarget -> colors.accentSoft
        isSelected -> colors.cellFocus
        cell.status == CellStatus.GIVEN -> colors.cellGiven
        cell.status == CellStatus.CORRECT -> colors.cellCorrect
        cell.status == CellStatus.INCORRECT -> colors.cellIncorrect
        cell.status == CellStatus.REVEALED -> colors.cellRevealed
        isInWord -> colors.cellWord
        else -> colors.cellFill
    }
    val fill by animateColorAsState(targetFill, tween(180), label = "cellFill")

    val borderColor = when {
        isDropTarget || isSelected -> colors.accent
        isInWord -> colors.accent.copy(alpha = 0.35f)
        else -> Color.Transparent
    }
    // The target swells slightly under the finger, which reads through a hand
    // covering half the board better than a colour change alone.
    val lift by animateFloatAsState(if (isDropTarget) 1.06f else 1f, tween(140), label = "cellLift")

    // A freshly typed letter pops in; an empty cell has nothing to animate, and a
    // starter letter was never typed at all.
    val letter = cell.userChar
    val pop = remember(cell.pos) { Animatable(1f) }
    LaunchedEffect(letter) {
        if (letter != null && !cell.isGiven) {
            pop.snapTo(0.55f)
            pop.animateTo(1f, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessHigh))
        }
    }

    // Reported so a dragged letter knows what it is over, and so a letter taken
    // off the board knows where to fly from. The report sits before the scale
    // layer below, so the target rect stays the square's settled bounds.
    if (dragState != null) {
        DisposableEffect(dragState, cell.pos) {
            onDispose { dragState.forgetCell(cell.pos) }
        }
    }
    val boundsSink = if (dragState != null) {
        Modifier.onGloballyPositioned { dragState.registerCell(cell.pos, it.boundsInRoot()) }
    } else {
        Modifier
    }

    val shape = RoundedCornerShape(size * 0.16f)
    Box(
        modifier = Modifier
            .size(size)
            .then(boundsSink)
            .graphicsLayer {
                scaleX = lift
                scaleY = lift
            }
            .clip(shape)
            .background(fill)
            .border(if (isDropTarget || isSelected) 2.5.dp else 1.dp, borderColor, shape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        cell.number?.let { number ->
            Text(
                text = number.toString(),
                fontSize = (size.value * 0.24f).coerceIn(7f, 13f).sp,
                lineHeight = (size.value * 0.26f).coerceIn(8f, 14f).sp,
                fontWeight = FontWeight.SemiBold,
                color = colors.cellNumber,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(start = size * 0.08f, top = size * 0.04f),
            )
        }
        if (letter != null) {
            Text(
                text = letter.toString(),
                fontSize = (size.value * 0.52f).coerceIn(13f, 34f).sp,
                fontWeight = FontWeight.Bold,
                // A starter letter reads as part of the printed grid rather than
                // as something the player put there.
                color = if (cell.isGiven) colors.inkMuted else colors.cellText,
                textAlign = TextAlign.Center,
                modifier = Modifier.graphicsLayer {
                    scaleX = pop.value
                    scaleY = pop.value
                },
            )
        }
    }
}
